/**
 * Server-error presentation helpers.
 *
 * Dependency-free on purpose, like `toastBus.js` — this is read from inside
 * component catch blocks and must never drag the axios instance (and therefore
 * a module cycle with `authService`) along with it.
 */

/**
 * The most useful human-readable string an error carries, in precedence order.
 *
 * `message` first: `GlobalExceptionHandler.problem()` sets it alongside `detail`
 * on every exception it maps (BUG-016), specifically so the SPA can read one
 * field. `detail` second, because not every ProblemDetail on the wire comes from
 * that handler — Spring raises its own for faults resolved before the advice, and
 * those carry `detail` with no `message`. Reading only the first field turns those
 * into the generic fallback.
 *
 * Since issue #176 a bean-validation failure IS mapped by the handler and arrives
 * with both fields set to a per-field summary ("Request validation failed: name
 * must not be blank"), so no caller needs to read the structured `errors` array to
 * show the user something actionable.
 *
 * Flattening these to a generic string is not a cosmetic loss: the catalog and
 * distribution endpoints answer with the one thing the admin can act on
 * ("managed by the master catalog", "A template with this name already exists",
 * "Cannot distribute: unknown user id(s) [999]"), and a generic string makes a
 * 409 indistinguishable from a network blip.
 *
 * @param {unknown} err a rejected axios error (or anything else)
 * @param {string} fallback copy to use when the server said nothing useful
 * @returns {string}
 */
export const serverMessage = (err, fallback) =>
  err?.response?.data?.message ?? err?.response?.data?.detail ?? fallback;
